/**
 * SessionDialog Component
 *
 * Reusable dialog for creating and editing timetable sessions.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTimetableMutations } from '@/hooks/useTimetable'
import { Loader2, Calendar } from 'lucide-react'

const DAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
]

export default function SessionDialog({
  open,
  onOpenChange,
  session,
  defaultDay,
  sessionTypes,
  onSave
}) {
  const isEditing = !!session
  const { createSession, updateSession, loading } = useTimetableMutations()

  // Form state
  const [sessionTypeId, setSessionTypeId] = useState('')
  const [dayOfWeek, setDayOfWeek] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState(null)

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      if (session) {
        // Editing existing session
        setSessionTypeId(session.session_type_id)
        setDayOfWeek(String(session.day_of_week))
        setStartTime(session.start_time?.substring(0, 5) || '') // Extract HH:MM from HH:MM:SS
        setEndTime(session.end_time?.substring(0, 5) || '')
        setLocation(session.location || '')
        setNotes(session.notes || '')
      } else {
        // Creating new session
        setSessionTypeId('')
        setDayOfWeek(defaultDay !== null ? String(defaultDay) : '')
        setStartTime('')
        setEndTime('')
        setLocation('')
        setNotes('')
      }
      setError(null)
    }
  }, [open, session, defaultDay])

  const handleSave = async () => {
    // Validation
    if (!sessionTypeId) {
      setError('Session type is required')
      return
    }
    if (dayOfWeek === '') {
      setError('Day of week is required')
      return
    }
    if (!startTime) {
      setError('Start time is required')
      return
    }
    if (!endTime) {
      setError('End time is required')
      return
    }

    // Validate time range
    if (startTime >= endTime) {
      setError('End time must be after start time')
      return
    }

    setError(null)

    try {
      const sessionData = {
        session_type_id: sessionTypeId,
        day_of_week: parseInt(dayOfWeek, 10),
        start_time: startTime + ':00', // Add seconds
        end_time: endTime + ':00',
        location: location.trim() || null,
        notes: notes.trim() || null,
        is_active: true
      }

      if (isEditing) {
        await updateSession(session.id, sessionData)
      } else {
        await createSession(sessionData)
      }

      onSave?.()
      onOpenChange(false)
    } catch (err) {
      console.error('[SessionDialog] Save error:', err)
      setError(err.message || 'Failed to save session')
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {isEditing ? 'Edit Session' : 'Add Session'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Session Type */}
          <div className="space-y-2">
            <Label htmlFor="session-type">
              Session Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={sessionTypeId}
              onValueChange={setSessionTypeId}
              disabled={loading}
            >
              <SelectTrigger id="session-type">
                <SelectValue placeholder="Select session type..." />
              </SelectTrigger>
              <SelectContent>
                {sessionTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: type.colour || '#9CA3AF' }}
                      />
                      {type.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day of Week */}
          <div className="space-y-2">
            <Label htmlFor="day-of-week">
              Day of Week <span className="text-destructive">*</span>
            </Label>
            <Select
              value={dayOfWeek}
              onValueChange={setDayOfWeek}
              disabled={loading}
            >
              <SelectTrigger id="day-of-week">
                <SelectValue placeholder="Select day..." />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map(day => (
                  <SelectItem key={day.value} value={String(day.value)}>
                    {day.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-time">
                Start Time <span className="text-destructive">*</span>
              </Label>
              <Input
                id="start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-time">
                End Time <span className="text-destructive">*</span>
              </Label>
              <Input
                id="end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">
              Location <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Laboratory, Grow Rooms..."
              disabled={loading}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this session..."
              rows={2}
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Session'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
